import { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { StatusFilterValue } from '../../types/dashboard';

interface StatusFilterProps {
  value: StatusFilterValue;
  onChange: (value: StatusFilterValue) => void;
}

const STATUS_LABELS = {
  potentiel: 'Potentiel',
  compromis: 'Compromis',
  acte: 'Acté',
  encaisse: 'Encaissé',
  cloture: 'Clôturé'
};

const STATUS_DOTS: Record<string, string> = {
  potentiel: 'bg-amber-400',
  compromis: 'bg-blue-400',
  acte:      'bg-violet-400',
  encaisse:  'bg-emerald-400',
  cloture:   'bg-slate-400',
};

export default function StatusFilter({ value, onChange }: StatusFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedCount = Object.values(value).filter(Boolean).length;
  const allSelected = Object.values(value).every(Boolean);

  const toggleAll = () => {
    const newValue = !allSelected;
    onChange(Object.keys(value).reduce((acc, key) => ({ ...acc, [key]: newValue }), {} as StatusFilterValue));
  };

  const hasFilter = !allSelected && selectedCount > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400/40 bg-white ${hasFilter ? 'border-indigo-300 text-slate-800' : 'border-slate-200 text-slate-500'}`}
      >
        <span className="truncate max-w-[160px]">
          {selectedCount === 0 ? 'Statuts' :
           allSelected ? 'Tous les statuts' :
           `${selectedCount} statut${selectedCount > 1 ? 's' : ''}`}
        </span>
        {hasFilter && (
          <span className="flex items-center justify-center w-4 h-4 rounded-full bg-indigo-600 text-white text-xs font-bold shrink-0">
            {selectedCount}
          </span>
        )}
        <ChevronDown size={14} className={`text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-30 top-full mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <div
            className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
            onClick={toggleAll}
          >
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Tout sélectionner</span>
            {allSelected && <Check size={14} className="text-indigo-600" />}
          </div>
          {Object.entries(value).map(([status, checked]) => (
            <div
              key={status}
              className={`flex items-center justify-between px-3 py-2.5 cursor-pointer transition-colors ${checked ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
              onClick={() => onChange({ ...value, [status]: !checked })}
            >
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOTS[status] ?? 'bg-slate-400'}`} />
                <span className={`text-sm ${checked ? 'text-indigo-700 font-medium' : 'text-slate-700'}`}>
                  {STATUS_LABELS[status as keyof typeof STATUS_LABELS]}
                </span>
              </div>
              {checked && <Check size={14} className="text-indigo-600 shrink-0" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
